// LIBRARIES
import * as Sentry from "@sentry/react";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useGeneralContext } from "../../context/AppGeneralContext";
import { useT } from "@transifex/react";
import queryString from "query-string";
import { useLogger } from "../../hooks/useLogger";
import type { AssistantHandoffState } from "../../@types/anonymousFlow";
import { SSO_PROVIDERS, type SsoProvider } from "../../constants/SsoConstants";
import { trackTouchpointPageView } from "../../networking/touchpoint";
import { Wrapper } from "./StyledComponents";
import ContentWithHeader from "../ContentWithHeader/ContentWithHeader";
import { parseBookDeepLinkParams, storeBookDeepLinkParams } from "../../helpers/DeepLinkParams";

export interface AuthWrapperProps {}

/** Provider from buildSsoRegisterUrl's `sso` marker, if this visit is the return from Google/Apple/fobizz. */
const parseSsoProvider = (value: unknown): SsoProvider | undefined =>
    SSO_PROVIDERS.find((provider) => provider === value);

const AuthWrapper: React.FC<AuthWrapperProps> = () => {
    const { successfulAuth, loginUser, loginUserAfterSso, isIframeMode, setIsIframeMode } = useGeneralContext();
    const { logEvent } = useLogger();

    const { search } = useLocation();
    const urlParams = queryString.parse(search.replace("?", ""));
    const bookDeepLink = parseBookDeepLinkParams(urlParams);
    const ssoProvider = parseSsoProvider(urlParams.sso);

    const t = useT();
    const t_loadingText = t("Loading", { _tags: "AuthWrapper" });
    const [isLoginInProgress, setIsLoginInProgress] = useState(true);

    const history = useHistory<AssistantHandoffState>();
    /** Prevents duplicate loginUser() calls caused by React 18 StrictMode double-mounting.
     *  Without this, the redirect to /create (redirect screen) triggers twice,
     *  breaking navigation especially for deepLinks, such as from Cornelsen, etc. */
    const loginInitiated = useRef(false);

    /**
     * No session after Google/Apple/fobizz (provider error or cancel, or the login itself failed): the teacher continues
     * anonymously, and the localStorage handoff is claimed on a later login.
     */
    const continueAfterSsoFailure = (provider: SsoProvider, error: string) => {
        logEvent("failed sso login", { provider, error });
        history.replace("/assistent", { ssoLoginFailedProvider: provider });
    };

    const handleSsoError = (provider: SsoProvider) => {
        const error = String(urlParams.error);
        Sentry.addBreadcrumb({
            category: "auth",
            message: "SSO login failed",
            data: { provider, error, errorDescription: urlParams.error_description },
        });
        continueAfterSsoFailure(provider, error);
    };

    // Both outcomes navigate away, so isLoginInProgress is never reset here.
    const handleSsoLogin = (provider: SsoProvider) => {
        trackTouchpointPageView("auth");
        loginUserAfterSso(provider).catch((error) => {
            // Unlike a provider error, the account may exist here, so this one is worth an alert.
            Sentry.captureException(error, { extra: { stage: "loginUserAfterSso", provider } });
            continueAfterSsoFailure(provider, "login_failed");
        });
    };

    useEffect(() => {
        if (loginInitiated.current) return;
        loginInitiated.current = true;

        if (isIframeMode) {
            history.push("/create/anonymous");
        } else if (ssoProvider && urlParams.error) {
            handleSsoError(ssoProvider);
        } else if (ssoProvider) {
            handleSsoLogin(ssoProvider);
        } else {
            trackTouchpointPageView("auth");
            let redirect = "";
            if (urlParams.redirectTo) {
                redirect = urlParams.redirectTo as string;
            }
            loginUser(redirect)
                .catch(() => {
                    if (bookDeepLink) {
                        const params = queryString.stringify({
                            articleUuid: bookDeepLink.articleUuid,
                            unitFromUuid: bookDeepLink.unitFromUuid,
                            unitToUuid: bookDeepLink.unitToUuid,
                            fullScreen: "true",
                        });
                        history.push("/create/anonymous?" + params);
                    } else {
                        history.push("/unauthorized");
                    }
                })
                .finally(() => {
                    setIsLoginInProgress(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (urlParams.iframeMode) {
            setIsIframeMode(true);
        }
        if (urlParams.articleId) {
            const aId: string = urlParams.articleId as string;
            localStorage.setItem("initAID", aId);
        }
        if (bookDeepLink) {
            storeBookDeepLinkParams(bookDeepLink);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParams]);

    const renderPage = function () {
        if (!isLoginInProgress && !successfulAuth) {
            history.push("/unauthorized");
        } else {
            return <p> .... {t_loadingText}</p>;
        }
    };

    return (
        <ContentWithHeader id={"content-with-header"}>
            <Wrapper>{renderPage()}</Wrapper>
        </ContentWithHeader>
    );
};

export default AuthWrapper;
